import { compareRetailerPreference } from '../domain/retailerPreference';
// BatterySelection.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../FishFinderBuilder.css';
import { batteriesData } from '../data/productData';
import ImageModal from './ImageModal';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { fetchProductPrices, fetchProductNamesWithOffers, resolveProductNameForSupabase } from '../utils/api';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  @media (min-width: 769px) {
    height: 100px;
  }
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;

  @media (min-width: 769px) {
    width: 95%;
    height: 95%;
  }
`;

const BatterySelection = ({
  battery = null,
  setBattery = () => {},
  prevStep = () => {},
  nextStep = () => {},
  resetAll = () => {},
  jumpToStep = () => {},
  isInModal = false,
  onNavigateToFullPage = () => {}
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get builder state from location (for screen info); memoize so effect deps are stable
  const builderState = useMemo(() => location.state || {}, [location.state]);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Debug logging to check what state is being received
  useEffect(() => {
    console.log("BuilderState received in BatterySelection:", builderState);
    console.log("Transducer info:", builderState?.transducer);
  }, [builderState]);
  
  // Track which images have already tried fallbacks to prevent infinite loops
  const [failedImages, setFailedImages] = useState({});
  
  // Add modal state
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });

  // Track expanded battery cards
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Track selected battery including the variant (with/without charger)
  const [selectedVariantId, setSelectedVariantId] = useState(battery?.id || null);
  
  // Add state for onboarding features
  const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
  const [shouldGlowShowMoreSpecs, setShouldGlowShowMoreSpecs] = useState(false);
  const filtersPanelRef = useRef(null);
  
  // Function to check if there are active filters
  const hasActiveFilters = () => {
    return (filters?.type && filters.type.length < 2) || 
           filters?.bluetooth || 
           (filters?.minRuntime && filters.minRuntime > 0);
  };
  
  // Add state for battery pricing data
  const [batteryPricing, setBatteryPricing] = useState({});
  
  // Add charger toggle state for each battery, with default state based on any existing selection
  const [batteryStates, setBatteryStates] = useState(() => {
    // Start with only batteries that have charger options set to "without charger"
    const initialState = batteriesData.reduce((acc, curr) => {
      // Only add to state if the battery has charger options
      if (curr.hasChargerOption) {
        acc[curr.id] = { includeCharger: false };
      }
      return acc;
    }, {});

    // If there's a selected battery with charger, update its state
    if (battery && battery.includeCharger) {
      // Find the base battery ID that corresponds to this variant
      const baseBatteryId = batteriesData.find(b => 
        // Match by name prefix since variant names typically include the base name
        (battery.name && battery.name.includes(b.name)) ||
        // Or match by ID prefix for batteries with variant IDs
        (b.hasChargerOption && b.variants.withCharger.id === battery.id) ||
        (b.hasChargerOption && b.variants.withoutCharger.id === battery.id) ||
        // Or direct ID match for non-variant batteries
        b.id === battery.id
      )?.id;
      
      // If we found a matching battery with charger options, set its charger state
      if (baseBatteryId && batteriesData.find(b => b.id === baseBatteryId)?.hasChargerOption) {
        initialState[baseBatteryId] = { includeCharger: true };
      }
    }
    
    return initialState;
  });

  // Log the initial state and debug information on component mount
  useEffect(() => {
    if (battery) {
      console.log("Initial battery:", battery);
      console.log("Initial selectedVariantId:", selectedVariantId);
      console.log("Initial batteryStates:", batteryStates);
      
      // If the battery has a different variant ID than what's currently selected,
      // update the selectedVariantId
      if (battery.id !== selectedVariantId) {
        setSelectedVariantId(battery.id);
      }
    }
  }, [battery, batteryStates, selectedVariantId]);

  // Fetch pricing data for batteries with csvLookupId
  useEffect(() => {
    const fetchBatteryPrices = async () => {
      for (const batteryOption of batteriesData) {
        // Check main battery for csvLookupId
        if (batteryOption.csvLookupId) {
          try {
            const result = await fetchProductPrices(
              batteryOption.id,
              0,
              3,
              batteryOption.csvLookupId,
              true // exact match only
            );
            
            if (result.success && result.data.length > 0) {
              const sortedData = result.data.sort((a, b) => {
                let priceA = parseFloat(a.Price?.replace(/[$,]/g, '') || '0');
                let priceB = parseFloat(b.Price?.replace(/[$,]/g, '') || '0');
                
                                    // Apply Amped Outdoors discount if applicable
                    if (batteryOption.dealInfo && batteryOption.dealInfo.includes('10% OFF at checkout')) {
                      if (a.Source && a.Source.toLowerCase().includes('amped outdoors')) {
                        priceA = priceA * 0.9;
                      }
                      if (b.Source && b.Source.toLowerCase().includes('amped outdoors')) {
                        priceB = priceB * 0.9;
                      }
                    }
                
                return priceA - priceB || compareRetailerPreference(a.Source, b.Source);
              });
              
                                const lowestPrice = parseFloat(sortedData[0].Price?.replace(/[$,]/g, '') || '0');
                  const isAmpedSource = sortedData[0].Source && sortedData[0].Source.toLowerCase().includes('amped outdoors');
                  const finalPrice = (batteryOption.dealInfo && batteryOption.dealInfo.includes('10% OFF at checkout') && isAmpedSource) 
                                   ? lowestPrice * 0.9 : lowestPrice;
              
              // Calculate savings vs MSRP
              const msrpSavings = batteryOption.msrp && finalPrice < batteryOption.msrp 
                ? {
                    amount: (batteryOption.msrp - finalPrice).toFixed(2),
                    percent: Math.round(((batteryOption.msrp - finalPrice) / batteryOption.msrp) * 100),
                    reference: 'MSRP'
                  }
                : null;
              
              setBatteryPricing(prev => ({
                ...prev,
                [batteryOption.id]: {
                  price: finalPrice.toFixed(2),
                  msrpSavings: msrpSavings,
                  source: sortedData[0].Source,
                  lastUpdated: new Date().toISOString()
                }
              }));
            }
          } catch (error) {
            console.error(`Error fetching prices for battery ${batteryOption.id}:`, error);
          }
        }

        // Check variants for csvLookupId
        if (batteryOption.variants) {
          for (const [, variant] of Object.entries(batteryOption.variants)) {
            if (variant.csvLookupId) {
              try {
                const result = await fetchProductPrices(
                  variant.id,
                  0,
                  3,
                  variant.csvLookupId,
                  true // exact match only
                );
                
                if (result.success && result.data.length > 0) {
                  const sortedData = result.data.sort((a, b) => {
                    let priceA = parseFloat(a.Price?.replace(/[$,]/g, '') || '0');
                    let priceB = parseFloat(b.Price?.replace(/[$,]/g, '') || '0');
                    
                    // Apply Amped Outdoors discount if applicable
                    if (batteryOption.dealInfo && batteryOption.dealInfo.includes('10% OFF at checkout')) {
                      if (a.Source && a.Source.toLowerCase().includes('amped outdoors')) {
                        priceA = priceA * 0.9;
                      }
                      if (b.Source && b.Source.toLowerCase().includes('amped outdoors')) {
                        priceB = priceB * 0.9;
                      }
                    }
                    
                    return priceA - priceB || compareRetailerPreference(a.Source, b.Source);
                  });
                  
                  const lowestPrice = parseFloat(sortedData[0].Price?.replace(/[$,]/g, '') || '0');
                  const isAmpedSource = sortedData[0].Source && sortedData[0].Source.toLowerCase().includes('amped outdoors');
                  const finalPrice = (batteryOption.dealInfo && batteryOption.dealInfo.includes('10% OFF at checkout') && isAmpedSource) 
                                   ? lowestPrice * 0.9 : lowestPrice;
                  
                  // Calculate savings vs MSRP
                  const msrpSavings = variant.msrp && finalPrice < variant.msrp 
                    ? {
                        amount: (variant.msrp - finalPrice).toFixed(2),
                        percent: Math.round(((variant.msrp - finalPrice) / variant.msrp) * 100),
                        reference: 'MSRP'
                      }
                    : null;
                  
                  setBatteryPricing(prev => ({
                    ...prev,
                    [variant.id]: {
                      price: finalPrice.toFixed(2),
                      msrpSavings: msrpSavings,
                      source: sortedData[0].Source,
                      lastUpdated: new Date().toISOString()
                    }
                  }));
                }
              } catch (error) {
                console.error(`Error fetching prices for variant ${variant.id}:`, error);
              }
            }
          }
        }
      }
    };

    fetchBatteryPrices();
  }, []);

  // Helper function to format price
  const formatPrice = (price) => {
    if (!price) return '0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numPrice.toFixed(2);
  };

  // Handle image errors with fallback images from the data
  const handleImageError = (e, batteryName) => {
    if (failedImages[batteryName]) return;
    
    console.error(`Image failed to load for ${batteryName}: ${e.target.src}`);
    setFailedImages(prev => ({...prev, [batteryName]: true}));
    
    const batteryData = batteriesData.find(b => b.name === batteryName);
    
    if (batteryData && batteryData.fallbackImage) {
      e.target.src = batteryData.fallbackImage;
      return;
    }
    
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${batteryName.replace(/\s+/g, '+')}`;
    e.target.onerror = null;
  };

  // Handle selection of a battery
  const handleBatterySelect = (selectedBattery) => {
    // Create the battery object with all necessary properties
    let batteryToSelect;
    let variantId;
    
    if (selectedBattery.hasChargerOption) {
      // For batteries with charger options, check the charger state
      const includeCharger = batteryStates[selectedBattery.id]?.includeCharger || false;
      const variant = includeCharger ? selectedBattery.variants.withCharger : selectedBattery.variants.withoutCharger;
      variantId = variant.id;
      batteryToSelect = {
        ...variant,
        image: selectedBattery.image,
        fallbackImage: selectedBattery.fallbackImage,
        id: variant.id,
        hasChargerOption: true,
        includeCharger: includeCharger,
        type: selectedBattery.type,
        capacity: selectedBattery.capacity,
        nominalVoltage: selectedBattery.nominalVoltage,
        wattHours: selectedBattery.wattHours
      };
    } else {
      // For batteries without charger options (charger always included)
      variantId = selectedBattery.id;
      batteryToSelect = {
        ...selectedBattery,
        hasChargerOption: false,
        includeCharger: true // Always true for batteries with charger included
      };
    }
    
    // Update selected variant ID for UI visual feedback
    setSelectedVariantId(variantId);
    
    // Then set the battery in the parent component
    setBattery(batteryToSelect);
  };

  // Handle charger toggle for a specific battery
  const handleChargerToggle = (batteryOption, include) => {
    // Update the charger state
    setBatteryStates(prev => ({
      ...prev,
      [batteryOption.id]: { ...prev[batteryOption.id], includeCharger: include }
    }));

    // Get the current variant ID
    const currentVariant = batteryOption.hasChargerOption
      ? (include ? batteryOption.variants.withCharger : batteryOption.variants.withoutCharger)
      : batteryOption;
    
    // Only update the current battery selection if this exact battery variant was previously selected
    if (selectedVariantId === (include ? batteryOption.variants.withCharger.id : batteryOption.variants.withoutCharger.id) ||
        selectedVariantId === batteryOption.id) {
      // Update the selected variant ID
      setSelectedVariantId(currentVariant.id);
      
      // Update the battery in the parent component
    if (batteryOption.hasChargerOption) {
      setBattery({
          ...currentVariant,
        image: batteryOption.image,
          fallbackImage: batteryOption.fallbackImage,
          hasChargerOption: true,
          includeCharger: include,
          type: batteryOption.type,
          capacity: batteryOption.capacity,
          nominalVoltage: batteryOption.nominalVoltage,
          wattHours: batteryOption.wattHours
        });
      }
    }
  };

  // Add filter state
  const [filters, setFilters] = useState({});
  
  // Product names that have at least one active offer (hide Amped-only etc. from selection)
  const [productNamesWithOffers, setProductNamesWithOffers] = useState(null);
  useEffect(() => {
    fetchProductNamesWithOffers().then(setProductNamesWithOffers);
  }, []);

  // When offers list loads, ensure we don't have a variant selected that has no offer (e.g. 15Ah + charger)
  useEffect(() => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return;
    setBatteryStates(prev => {
      let next = { ...prev };
      let changed = false;
      batteriesData.forEach(battery => {
        if (!battery.hasChargerOption || !battery.variants) return;
        const withOk = variantHasActiveOffer(battery, 'withCharger');
        const withoutOk = variantHasActiveOffer(battery, 'withoutCharger');
        const currentInclude = prev[battery.id]?.includeCharger ?? false;
        if (currentInclude && !withOk && withoutOk) {
          next[battery.id] = { ...next[battery.id], includeCharger: false };
          changed = true;
        } else if (!currentInclude && !withoutOk && withOk) {
          next[battery.id] = { ...next[battery.id], includeCharger: true };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- variantHasActiveOffer depends on productNamesWithOffers (synced via closure)
  }, [productNamesWithOffers]);

  // If the currently selected variant has no offer, switch to the variant that has an offer (runs after batteryStates sync)
  const selectedVariantHasNoOffer = (() => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return false;
    for (const battery of batteriesData) {
      if (!battery.variants) {
        if (battery.id === selectedVariantId && battery.csvLookupId && !productNamesWithOffers.has(resolveProductNameForSupabase(battery.csvLookupId))) return true;
        continue;
      }
      for (const key of ['withCharger', 'withoutCharger']) {
        const v = battery.variants[key];
        if (v?.id === selectedVariantId && v.csvLookupId && !productNamesWithOffers.has(resolveProductNameForSupabase(v.csvLookupId))) return true;
      }
    }
    return false;
  })();
  useEffect(() => {
    if (!selectedVariantHasNoOffer) return;
    for (const battery of batteriesData) {
      if (!battery.hasChargerOption || !battery.variants) continue;
      const withOk = variantHasActiveOffer(battery, 'withCharger');
      const withoutOk = variantHasActiveOffer(battery, 'withoutCharger');
      if (selectedVariantId === battery.variants.withCharger.id && !withOk && withoutOk) {
        const v = battery.variants.withoutCharger;
        setBattery({ ...v, image: battery.image, fallbackImage: battery.fallbackImage, hasChargerOption: true, includeCharger: false, type: battery.type, capacity: battery.capacity, nominalVoltage: battery.nominalVoltage, wattHours: battery.wattHours });
        setSelectedVariantId(v.id);
        return;
      }
      if (selectedVariantId === battery.variants.withoutCharger.id && !withoutOk && withOk) {
        const v = battery.variants.withCharger;
        setBattery({ ...v, image: battery.image, fallbackImage: battery.fallbackImage, hasChargerOption: true, includeCharger: true, type: battery.type, capacity: battery.capacity, nominalVoltage: battery.nominalVoltage, wattHours: battery.wattHours });
        setSelectedVariantId(v.id);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setBattery is stable; variantHasActiveOffer tied to productNamesWithOffers
  }, [productNamesWithOffers, selectedVariantId, selectedVariantHasNoOffer]);

  const batteryHasActiveOffer = (battery) => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return true;
    if (battery.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(battery.csvLookupId))) return true;
    if (battery.variants) {
      for (const key of Object.keys(battery.variants)) {
        const v = battery.variants[key];
        if (v?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(v.csvLookupId))) return true;
      }
    }
    return false;
  };

  // Check if a specific variant (withCharger / withoutCharger) has an active offer
  const variantHasActiveOffer = (battery, variantKey) => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return true;
    const v = battery.variants?.[variantKey];
    if (!v?.csvLookupId) return false;
    return productNamesWithOffers.has(resolveProductNameForSupabase(v.csvLookupId));
  };

  // Create filtered batteries array
  const [filteredBatteries, setFilteredBatteries] = useState(batteriesData);
  
  // Update filtered batteries when filters change
  useEffect(() => {
    let filtered = batteriesData.filter(battery => {
      // Only show batteries that have at least one active offer (hide Amped-only after deactivation)
      if (!batteryHasActiveOffer(battery)) return false;
      // Filter by battery type
      if (filters.type && !filters.type.includes(battery.type)) {
        return false;
      }
      
      // Filter by Bluetooth capability
      if (filters.bluetooth && !battery.hasBluetooth) {
        return false;
      }
      
      // Filter by minimum runtime
      if (filters.minRuntime && builderState?.selectedScreen) {
        // Calculate runtime for this battery
        const wattHours = parseFloat(battery.wattHours.replace(/[^\d.]/g, ''));
        
        // Get power draw in watts (Amps * 12V)
        const screenPowerDrawStr = builderState.selectedScreen?.powerDraw12V || '1.0';
        const screenPowerDrawMatch = screenPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
        const screenPowerDrawAmps = screenPowerDrawMatch 
          ? (parseFloat(screenPowerDrawMatch[1]) + parseFloat(screenPowerDrawMatch[3])) / 2 
          : parseFloat(screenPowerDrawStr.replace(/[^\d.]/g, '') || 1.0);
        
        // Add transducer power draw if present
        const transducerPowerDrawStr = builderState.transducer?.powerDraw12V || '0';
        const transducerPowerDrawMatch = transducerPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
        const transducerPowerDrawAmps = transducerPowerDrawMatch 
          ? (parseFloat(transducerPowerDrawMatch[1]) + parseFloat(transducerPowerDrawMatch[3])) / 2 
          : parseFloat(transducerPowerDrawStr.replace(/[^\d.]/g, '') || 0);
        
        // Convert amps to watts using typical 12V system and combine
        const totalPowerDrawWatts = (screenPowerDrawAmps + transducerPowerDrawAmps) * 12;
        
        // Runtime calculation with 80% battery capacity
        const runtimeHours = (wattHours * 0.8) / totalPowerDrawWatts;
        
        // Check if runtime meets the minimum requirement
        if (runtimeHours < filters.minRuntime) {
          return false;
        }
      }
      
      return true;
    });
    
    setFilteredBatteries(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- batteryHasActiveOffer derived from productNamesWithOffers
  }, [filters, builderState?.selectedScreen, builderState?.transducer, productNamesWithOffers]);

  // Show onboarding tooltip on first visit or if forced via URL parameter
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('batterySelectionOnboardingSeen');
    const urlParams = new URLSearchParams(window.location.search);
    const forceOnboarding = urlParams.get('showOnboarding') === 'true';
    
    if (!hasSeenOnboarding || forceOnboarding) {
      // Show tooltip after a short delay to let the page load
      const timer = setTimeout(() => {
        setShowOnboardingTooltip(true);
        // Mark as seen so it doesn't show again (unless forced)
        if (!forceOnboarding) {
          localStorage.setItem('batterySelectionOnboardingSeen', 'true');
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto-dismiss onboarding tooltip after 15 seconds
  useEffect(() => {
    if (showOnboardingTooltip) {
      const autoDismissTimer = setTimeout(() => {
        setShowOnboardingTooltip(false);
      }, 15000);
      
      return () => clearTimeout(autoDismissTimer);
    }
  }, [showOnboardingTooltip]);

  // Check if "Show More Specs" should glow
  useEffect(() => {
    const hasUsedShowMoreSpecs = localStorage.getItem('hasUsedShowMoreSpecsBattery');
    if (!hasUsedShowMoreSpecs) {
      setShouldGlowShowMoreSpecs(true);
    }
  }, []);

  // Function to hide onboarding tooltip when user engages with filters
  const hideOnboardingTooltip = () => {
    if (showOnboardingTooltip) {
      setTimeout(() => setShowOnboardingTooltip(false), 500);
    }
  };

  // Function to toggle card expansion
  const toggleCardExpansion = (batteryId, event) => {
    // Prevent the click from triggering parent elements
    event.stopPropagation();
    
    // Mark that user has used "Show More Specs" and stop glowing
    if (shouldGlowShowMoreSpecs) {
      localStorage.setItem('hasUsedShowMoreSpecsBattery', 'true');
      setShouldGlowShowMoreSpecs(false);
    }
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batteryId)) {
        newSet.delete(batteryId);
      } else {
        newSet.add(batteryId);
      }
      return newSet;
    });
  };

    return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      minHeight: '100vh'
    }}>
      {/* Back to Builder button - outside and above everything else */}
      <div style={{
        backgroundColor: '#000000',
        padding: '10px 0 0 0',
        paddingLeft: '24px'
      }}>
        <button
          onClick={prevStep}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0 0 10px 0',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      {/* Main Content with Sidebar */}
      <div style={{
        display: 'flex',
        margin: 0,
        padding: 0,
        width: '100%',
        backgroundColor: '#000000'
      }}>
        {/* Sidebar */}
        <div ref={filtersPanelRef} style={{
          width: '25%',
          backgroundColor: '#1a1a1a',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: showOnboardingTooltip ? '0 0 20px 4px rgba(52, 152, 219, 0.6), 2px 0 8px rgba(0, 0, 0, 0.2)' : '2px 0 8px rgba(0, 0, 0, 0.2)',
          animation: showOnboardingTooltip ? 'glowPulse 2s ease-in-out infinite' : 'none',
          position: 'sticky',
          top: '75px',
          height: 'calc(100vh - 75px)',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 15px',
          margin: 0,
          zIndex: 10,
          alignSelf: 'flex-start',
          transition: 'box-shadow 0.3s ease'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              margin: '0',
              color: '#ffffff',
              fontWeight: '600'
            }}>Filter Options</h3>
            {hasActiveFilters() && (
              <button
                onClick={() => {
                  hideOnboardingTooltip();
                  setFilters({});
                }}
                style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.8'}
              >
                Reset
              </button>
            )}
          </div>
          
            <div style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0 0 10px 0',
            flex: 1,
            maxWidth: '100%'
          }}>
            {/* Filters Section */}
            <div className="filter-section" style={{ 
              marginBottom: '15px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {/* Battery Type Filter */}
              <div style={{ marginBottom: '12px' }}>
                <h5 style={{ 
                  fontSize: '10px',
                  margin: '0 0 4px 0',
                  color: '#b8c7d9',
                  fontWeight: '400'
                }}>Battery Type</h5>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  flexWrap: 'wrap',
                  maxWidth: '100%'
                }}>
                  <label style={{
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!filters?.type || filters?.type.includes('LiFePO4')}
                      onChange={() => {
                        hideOnboardingTooltip();
                        setFilters(prev => {
                          const currentTypes = prev?.type || ['LiFePO4', 'NMC'];
                          const newTypes = currentTypes.includes('LiFePO4')
                            ? currentTypes.filter(t => t !== 'LiFePO4')
                            : [...currentTypes, 'LiFePO4'];
                          return { ...prev, type: newTypes.length ? newTypes : null };
                        });
                      }}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    LiFePO4
                  </label>
                  <label style={{
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!filters?.type || filters?.type.includes('NMC')}
                      onChange={() => {
                        hideOnboardingTooltip();
                        setFilters(prev => {
                          const currentTypes = prev?.type || ['LiFePO4', 'NMC'];
                          const newTypes = currentTypes.includes('NMC')
                            ? currentTypes.filter(t => t !== 'NMC')
                            : [...currentTypes, 'NMC'];
                          return { ...prev, type: newTypes.length ? newTypes : null };
                        });
                      }}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    NMC
                  </label>
            </div>
              </div>
              
              {/* Bluetooth Filter */}
              <div style={{ marginBottom: '12px' }}>
                <h5 style={{ 
                  fontSize: '11px',
                  margin: '0 0 4px 0',
            color: '#b8c7d9',
                  fontWeight: '400'
                }}>Features</h5>
                <label style={{
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters?.bluetooth || false}
                    onChange={(e) => {
                      hideOnboardingTooltip();
                      setFilters(prev => ({
                        ...prev,
                        bluetooth: e.target.checked
                      }));
                    }}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  Bluetooth Enabled
                </label>
              </div>
              
              {/* Min Runtime Filter */}
              {builderState?.selectedScreen && (
                <div style={{ marginBottom: '12px' }}>
                  <h5 style={{ 
                    fontSize: '11px',
                    margin: '0 0 4px 0',
                    color: '#b8c7d9',
                    fontWeight: '400'
                  }}>Minimum Runtime</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      type="range"
                      min="0"
                      max="48"
                      step="1"
                      value={filters?.minRuntime || 0}
                      onChange={(e) => {
                        hideOnboardingTooltip();
                        setFilters(prev => ({
                          ...prev,
                          minRuntime: parseInt(e.target.value)
                        }));
                      }}
                      style={{
                        width: '100%',
                        cursor: 'pointer'
                      }}
                    />
          <div style={{
                      fontSize: '11px',
                      color: '#e0e0e0',
            display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>Min: {filters?.minRuntime || 0} hours</span>
            <button
                        onClick={() => {
                          hideOnboardingTooltip();
                          setFilters(prev => ({ ...prev, minRuntime: 0 }));
                        }}
              style={{
                          background: 'transparent',
                border: 'none',
                          color: '#b8c7d9',
                          fontSize: '10px',
                cursor: 'pointer',
                          padding: '0',
                          textDecoration: 'underline'
                        }}
                      >
                        Reset
            </button>
                    </div>
                  </div>
            </div>
          )}


          </div>

            {/* Battery Runtime Calculator Heading */}
          <div style={{ 
              backgroundColor: '#131313',
              margin: '15px -12px 15px -12px',
              padding: '12px 12px 0 12px',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <h3 style={{ 
                fontSize: '13px',
                margin: '0 0 12px 0',
                color: '#ffffff',
                fontWeight: '600',
              display: 'flex', 
              alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ color: '#4CAF50', fontSize: '14px' }}>⚡</span>
                Battery Runtime
              </h3>

            {/* If a screen is selected, show power draw and runtime estimates */}
            {builderState?.selectedScreen ? (
              <>
                <div style={{ 
            marginBottom: '15px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  {/* Selected Screen */}
                  <h4 style={{ 
                    fontSize: '12px',
                    marginBottom: '8px',
                    color: '#b8c7d9',
                    fontWeight: '500'
                  }}>Selected Components</h4>
                  <div style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    padding: '8px',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#ffffff', 
                      fontWeight: '500',
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      lineHeight: '1.3'
                    }}>
                      {builderState.selectedScreen.name}
                    </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
                      marginTop: '4px'
                    }}>
                      <div style={{ fontSize: '10px', color: '#4CAF50', fontWeight: '500' }}>
                        Power Draw: <span style={{ color: '#ffffff' }}>
                          {builderState.selectedScreen.powerDraw12V || '0.8-1.2'} A @ 12V
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Show Transducer if selected */}
                  {builderState?.transducer && (
                    <div style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
              padding: '8px',
                      borderRadius: '6px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#ffffff', 
                        fontWeight: '500',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        lineHeight: '1.3'
                      }}>
                        {builderState.transducer.name}
            </div>
            <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '4px'
                      }}>
                        <div style={{ fontSize: '10px', color: '#4CAF50', fontWeight: '500' }}>
                          Power Draw: <span style={{ color: '#ffffff' }}>
                            {builderState.transducer.powerDraw12V || '0'} A @ 12V
                          </span>
            </div>
          </div>
                    </div>
                  )}

                  {/* Total Power Draw */}
                  {builderState?.transducer && (
            <div style={{
                      backgroundColor: builderState.transducer.type === 'livescope' ? 'rgba(255, 213, 79, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                      padding: '8px',
                      borderRadius: '6px',
                      marginTop: '8px',
                      marginBottom: '8px',
                      border: builderState.transducer.type === 'livescope' ? '1px solid rgba(255, 213, 79, 0.3)' : '1px solid rgba(76, 175, 80, 0.2)'
                    }}>
                      <div style={{ 
                        fontSize: '10px', 
                        color: builderState.transducer.type === 'livescope' ? '#ffd54f' : '#4CAF50', 
                        fontWeight: '500',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                      }}>
                        <span>Total Power Draw:</span>
                        <span>
                          {(() => {
                            // Calculate total power draw
                            const screenPowerDrawStr = builderState.selectedScreen?.powerDraw12V || '1.0';
                            const screenPowerDrawMatch = screenPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                            const screenPowerDrawAmps = screenPowerDrawMatch 
                              ? (parseFloat(screenPowerDrawMatch[1]) + parseFloat(screenPowerDrawMatch[3])) / 2 
                              : parseFloat(screenPowerDrawStr.replace(/[^\d.]/g, '') || 1.0);
                            
                            const transducerPowerDrawStr = builderState.transducer?.powerDraw12V || '0';
                            const transducerPowerDrawMatch = transducerPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                            const transducerPowerDrawAmps = transducerPowerDrawMatch 
                              ? (parseFloat(transducerPowerDrawMatch[1]) + parseFloat(transducerPowerDrawMatch[3])) / 2 
                              : parseFloat(transducerPowerDrawStr.replace(/[^\d.]/g, '') || 0);
                            
                            const totalPowerDraw = screenPowerDrawAmps + transducerPowerDrawAmps;
                            return `${totalPowerDraw.toFixed(1)} A @ 12V`;
                          })()}
                        </span>
                      </div>
                      
                      {builderState.transducer.type === 'livescope' && (
                        <div style={{ 
                          fontSize: '9px', 
                          color: '#e0e0e0', 
                          fontStyle: 'italic',
                          marginTop: '2px',
                          textAlign: 'center'
                        }}>
                          High power draw will significantly reduce battery runtime
            </div>
          )}
                    </div>
                  )}
                </div>

          <div style={{
                  backgroundColor: 'rgba(73, 177, 76, 0.05)', 
                  borderLeft: '2px solid #4CAF50',
                  padding: '10px 12px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  color: '#b8c7d9',
                  lineHeight: '1.4',
                  marginBottom: '12px'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}><strong style={{ color: '#4CAF50' }}>Battery Runtime Info:</strong></p>
                  <p style={{ margin: '0 0 8px 0' }}>Runtime estimates are shown on each battery based on the combined power draw of selected components.</p>
                  {builderState?.transducer?.type === 'livescope' && (
                    <p style={{ margin: '0 0 8px 0', color: '#ffd54f' }}>
                      <strong>Note:</strong> Live imaging transducers draw significant power, which can reduce battery runtime by 50% or more compared to standard transducers.
                    </p>
                  )}
                  <p style={{ margin: '0' }}>Calculations use 80% of battery capacity for a conservative estimate. Actual runtime may vary based on settings, brightness, range, and usage conditions.</p>
                </div>
              </>
            ) : (
              <div style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                padding: '15px',
                borderRadius: '6px',
            display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                color: '#b8c7d9',
                fontSize: '11px',
                lineHeight: '1.4',
            marginBottom: '12px'
          }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⚡</div>
                <p style={{ margin: '0 0 15px 0', fontSize: '11px' }}>
                  <strong style={{ color: '#ffffff' }}>No screen selected</strong>
                </p>
                <p style={{ margin: '0 0 15px 0', fontSize: '11px' }}>
                  Select a screen to see battery life estimates for each battery option.
                </p>
              <button
                  onClick={() => navigate('/compare-screens', {
                    state: { returnTo: '/battery-selection' }
                  })}
                style={{
                    background: '#4CAF50',
                  border: 'none',
                    color: '#fff',
                    fontSize: '11px',
                  cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '4px',
                  fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    width: '100%'
                  }}
                >
                  Select a Screen <span>→</span>
              </button>
              </div>
            )}
            </div>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div style={{
          width: '70%',
          padding: '0 20px 20px 20px',
        }}>
          <h1 style={{ 
            color: '#ffffff',
            fontSize: '22px',
            marginBottom: '10px',
            marginTop: '0px'
          }}>Battery Selection</h1>

          {filteredBatteries.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#b8c7d9',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <span style={{ fontSize: '24px', marginBottom: '12px' }}>🔋</span>
              <h3 style={{ 
                margin: '0 0 8px 0',
                color: '#ffffff',
                fontSize: '18px'
              }}>No Matching Batteries Found</h3>
              <p style={{ 
                margin: '0 0 16px 0',
                fontSize: '14px',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters to see more battery options. Consider changing the battery type or minimum runtime requirement.
              </p>
              <button
                onClick={() => setFilters({})}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: '#4CAF50',
                  border: '1px solid #4CAF50',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <GridContainer>
              {filteredBatteries.map(batteryOption => {
                // For batteries without charger options, includeCharger is not relevant
                const includeCharger = batteryOption.hasChargerOption ? 
                  batteryStates[batteryOption.id]?.includeCharger : false;
                
                // Get the appropriate variant based on charger selection
                const currentVariant = batteryOption.hasChargerOption
                  ? (includeCharger ? batteryOption.variants.withCharger : batteryOption.variants.withoutCharger)
                  : batteryOption;
                
                return (
                  <div 
                    id={`battery-card-${batteryOption.id}`}
                    key={batteryOption.id} 
                    style={{
                      backgroundColor: '#1a1a1a',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      width: '100%',
                      transition: 'box-shadow 0.3s ease'
                    }}
                  >
                    <ImageContainer 
                      onClick={() => setModalImage({ 
                        isOpen: true, 
                        url: batteryOption.image, 
                        alt: batteryOption.name 
                      })}
                    >
                      <ProductImage 
                        src={batteryOption.image} 
                        alt={batteryOption.name}
                        onError={(e) => handleImageError(e, batteryOption.name)}
                      />
                      {/* Bluetooth badge - icon + label */}
                      {batteryOption.hasBluetooth && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          backgroundColor: 'rgba(25, 118, 210, 0.9)',
                          borderRadius: '12px',
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }} title="Bluetooth">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5" />
                          </svg>
                          <span style={{ fontSize: '11px', color: 'white', fontWeight: '500' }}>Bluetooth</span>
                        </div>
                      )}
                      {/* Charger included - green pill when this variant comes with a charger */}
                      {((batteryOption.hasChargerOption && includeCharger) || (!batteryOption.hasChargerOption && batteryOption.chargerDetails)) && (
                        <div style={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '8px',
                          backgroundColor: 'rgba(76, 175, 80, 0.9)',
                          borderRadius: '12px',
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }} title="Charger included">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                          </svg>
                          <span style={{ fontSize: '11px', color: 'white', fontWeight: '500' }}>Charger included</span>
                        </div>
                      )}
                      {/* Charger purchased separately - gray pill, same white icon as "Charger included" */}
                      {batteryOption.hasChargerOption && !includeCharger && (
                        <div style={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '8px',
                          backgroundColor: 'rgba(100, 100, 100, 0.9)',
                          borderRadius: '12px',
                          padding: '4px 6px 4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          maxWidth: 'calc(100% - 16px)',
                          boxSizing: 'border-box'
                        }} title="Charger purchased separately">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                          </svg>
                          <span style={{ fontSize: '10px', color: 'white', fontWeight: '500', lineHeight: 1.2, wordBreak: 'break-word' }}>Charger sold separately</span>
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                      </div>
                    </ImageContainer>

                    <div style={{ padding: '12px' }}>
                      <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>
                        {currentVariant.name}
                      </h3>
                      {(() => {
                        const batteryPricingData = batteryPricing[currentVariant.id];
                        const hasLowestPrice = batteryPricingData && batteryPricingData.price;

                        if (hasLowestPrice) {
                          return (
                            <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                              <p style={{ color: '#4CAF50', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                                Best Price: ${formatPrice(batteryPricingData.price)}
                              </p>
                              {batteryPricingData.msrpSavings && (
                                <div style={{
                                  marginTop: '4px',
                                  fontSize: '12px',
                                  color: '#e67e22',
                                  fontWeight: '500'
                                }}>
                                  ↓ Save ${formatPrice(batteryPricingData.msrpSavings.amount)} off {batteryPricingData.msrpSavings.reference || 'MSRP'}
                                </div>
                              )}
                            </div>
                          );
                        } else if (currentVariant.csvLookupId) {
                          // Show loading for products with CSV lookup ID
                          return (
                            <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                              <div style={{ fontSize: '0.8em', color: '#4CAF50', marginTop: '2px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                📡 Loading best price...
                              </div>
                              <p style={{ color: '#b8c7d9', margin: '4px 0 0 0', fontSize: '0.8em' }}>
                                MSRP: ${formatPrice(currentVariant.msrp)}
                              </p>
                            </div>
                          );
                        } else {
                          // Show MSRP for products without CSV lookup ID
                          return (
                            <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                              <p style={{ color: '#4CAF50', margin: '0 0 4px 0' }}>
                                MSRP: ${formatPrice(currentVariant.msrp)}
                              </p>
                            </div>
                          );
                        }
                      })()}



                      {/* Charger option: toggles when both variants have offers, otherwise a single label */}
                      {batteryOption.hasChargerOption && (() => {
                        const withoutOk = variantHasActiveOffer(batteryOption, 'withoutCharger');
                        const withOk = variantHasActiveOffer(batteryOption, 'withCharger');
                        if (!withoutOk && !withOk) return null;
                        if (withoutOk && withOk) {
                          return (
                            <div style={{
                              display: 'flex',
                              gap: '1px',
                              marginBottom: '10px',
                              backgroundColor: '#233446',
                              padding: '1px',
                              borderRadius: '4px',
                              width: '100%'
                            }}>
                              <button
                                onClick={() => handleChargerToggle(batteryOption, false)}
                                style={{
                                  flex: 1,
                                  padding: '6px 8px',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  backgroundColor: !includeCharger ? '#4CAF50' : 'transparent',
                                  color: !includeCharger ? 'white' : '#b8c7d9',
                                  transition: 'all 0.2s ease',
                                  fontSize: '0.8em'
                                }}
                              >
                                Without Charger
                              </button>
                              <button
                                onClick={() => handleChargerToggle(batteryOption, true)}
                                style={{
                                  flex: 1,
                                  padding: '6px 8px',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  backgroundColor: includeCharger ? '#4CAF50' : 'transparent',
                                  color: includeCharger ? 'white' : '#b8c7d9',
                                  transition: 'all 0.2s ease',
                                  fontSize: '0.8em'
                                }}
                              >
                                With Charger
                              </button>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Battery Specs - Now with expandable section */}
                      <div className="battery-specs" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '0',
                        fontSize: '0.9em',
                        marginBottom: '10px'
                      }}>
                        {/* Always visible specs - Most important first */}
                        <div className="spec-row" style={{
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Charger:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>
                            {((batteryOption.hasChargerOption && includeCharger) || (!batteryOption.hasChargerOption && batteryOption.chargerDetails)) ? 'Included' : batteryOption.hasChargerOption ? 'Purchased separately' : '-'}
                          </span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Est. Runtime:</span>
                          {builderState?.selectedScreen ? (
                            (() => {
                              // Calculate runtime for this battery
                              const wattHours = parseFloat(batteryOption.wattHours.replace(/[^\d.]/g, ''));
                              
                              // Get screen power draw in amps
                              const screenPowerDrawStr = builderState.selectedScreen?.powerDraw12V || '1.0';
                              const screenPowerDrawMatch = screenPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                              const screenPowerDrawAmps = screenPowerDrawMatch 
                                ? (parseFloat(screenPowerDrawMatch[1]) + parseFloat(screenPowerDrawMatch[3])) / 2 
                                : parseFloat(screenPowerDrawStr.replace(/[^\d.]/g, '') || 1.0);
                              
                              // Add transducer power draw if present
                              const transducerPowerDrawStr = builderState.transducer?.powerDraw12V || '0';
                              const transducerPowerDrawMatch = transducerPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                              const transducerPowerDrawAmps = transducerPowerDrawMatch 
                                ? (parseFloat(transducerPowerDrawMatch[1]) + parseFloat(transducerPowerDrawMatch[3])) / 2 
                                : parseFloat(transducerPowerDrawStr.replace(/[^\d.]/g, '') || 0);
                              
                              // Convert amps to watts using typical 12V system
                              const totalPowerDrawWatts = (screenPowerDrawAmps + transducerPowerDrawAmps) * 12;
                              
                              // Runtime calculation with 80% battery capacity
                              const runtimeHours = (wattHours * 0.8) / totalPowerDrawWatts;
                              const hours = Math.floor(runtimeHours);
                              const minutes = Math.round((runtimeHours - hours) * 60);
                              
                              return (
                                <span className="spec-value" style={{ 
                                  color: '#4CAF50', 
                                  fontSize: '0.9em',
                                  fontWeight: '500'
                                }}>
                                  {hours}h {minutes}m
                                </span>
                              );
                            })()
                          ) : (
                            <span 
                              className="spec-value" 
                              style={{ 
                                color: '#4CAF50', 
                                fontSize: '0.9em', 
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}
                              onClick={() => navigate('/compare-screens', {
                                state: { returnTo: '/battery-selection' }
                              })}
                            >
                              Select a screen →
                            </span>
                          )}
                        </div>
                        <div className="spec-row" style={{
        display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Capacity:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.capacity}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Type:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.type}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Watt Hours:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.wattHours || '384Wh'}</span>
                        </div>

                        {/* Expandable specs section */}
                        {expandedCards.has(batteryOption.id) ? (
                          <>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Voltage:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.nominalVoltage || '12.8V'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Dimensions:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.dimensions || '5.9" x 3.9" x 5.1"'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Weight:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.weight || '6.8 lbs'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Cycles:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.chargeCycles || '800+'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Warranty:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.warranty || '3 years'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Bluetooth:</span>
                              <span className="spec-value" style={{ 
                                color: batteryOption.hasBluetooth ? '#4CAF50' : '#ffffff', 
                                fontSize: '0.9em',
                                fontWeight: batteryOption.hasBluetooth ? '500' : 'normal'
                              }}>
                                {batteryOption.hasBluetooth ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </>
                        ) : (
                          // Show a faded version of the 5th feature (Voltage) when not expanded
                          <div className="spec-row" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.6,
                            position: 'relative'
                          }}>
                            <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Voltage:</span>
                            <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.nominalVoltage || '12.8V'}</span>
                            {/* Gradient overlay to fade the content */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.8) 100%)',
                              pointerEvents: 'none'
                            }} />
                          </div>
                        )}
                        
                        {/* Show/Hide button */}
        <button
                          onClick={(e) => toggleCardExpansion(batteryOption.id, e)}
          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#b8c7d9',
                            width: '100%',
                            padding: '4px 0',
                            marginTop: '4px',
                            fontSize: '0.9em',
            display: 'flex',
            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            boxShadow: shouldGlowShowMoreSpecs && !expandedCards.has(batteryOption.id) ? 
                              '0 0 10px 2px rgba(52, 152, 219, 0.6)' : 'none',
                            animation: shouldGlowShowMoreSpecs && !expandedCards.has(batteryOption.id) ? 
                              'showMoreSpecsGlow 2s ease-in-out infinite' : 'none',
                            borderRadius: '4px',
                            transition: 'box-shadow 0.3s ease'
                          }}
                        >
                          {expandedCards.has(batteryOption.id) ? (
                            <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                          ) : (
                            <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                          )}
        </button>
                      </div>

                {/* Compact Styled Action Buttons - matching QuickScreenComparison format */}
        <div style={{
          marginTop: '8px',
          padding: '6px',
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '6px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '6px',
            color: '#ffffff',
            fontSize: '0.6em',
            fontWeight: '600',
            opacity: 0.9,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            Choose Your Next Step:
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {currentVariant.csvLookupId && (
              <div style={{ textAlign: 'center', width: '100%' }}>
                <button
                  onClick={() => {
                    // If priceComparisonUrl already includes the full path, use it directly
                    // Otherwise, construct the path with /simple-products/ prefix
                    const productUrl = currentVariant.priceComparisonUrl?.startsWith('/') 
                      ? currentVariant.priceComparisonUrl 
                      : `/simple-products/${currentVariant.priceComparisonUrl || currentVariant.id}`;
                    navigate(productUrl);
                  }}
                  style={{
                    width: '100%',
                    fontSize: '0.7em',
                    padding: '6px 8px',
                    background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '4px',
                    fontWeight: '600',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    margin: '0',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #34ce57 0%, #28a745 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ lineHeight: '1.0' }}>💰 Find Best Price</div>
                  <div style={{
                    fontSize: '0.8em',
                    fontWeight: '400',
                    opacity: 0.9,
                    marginTop: '1px',
                    lineHeight: '1.0'
                  }}>
                    Compare prices across stores
                  </div>
                </button>
              </div>
            )}
            
            <div style={{
              textAlign: 'center',
              fontSize: '0.55em',
              color: '#aaa',
              margin: '0',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              or
            </div>
            
            <button 
              onClick={() => {
                // First select the battery
                handleBatterySelect(batteryOption);
                
                // Then navigate to next step - use direct navigate if available
                if (isInModal) {
                  setTimeout(() => nextStep(), 50);
                } else {
                  // Use the current variant we already calculated above
                  const batteryToSave = {
                    ...currentVariant,
                    image: batteryOption.image,
                    fallbackImage: batteryOption.fallbackImage,
                    type: batteryOption.type,
                    capacity: batteryOption.capacity,
                    nominalVoltage: batteryOption.nominalVoltage,
                    wattHours: batteryOption.wattHours,
                    hasChargerOption: batteryOption.hasChargerOption,
                    includeCharger: includeCharger
                  };
                  
                  console.log("Navigating with battery:", batteryToSave);
                  console.log("Preserving existing state:", builderState);
                        
                  // Use React Router navigate directly to custom-builder with state
                  navigate('/custom-builder', { 
                    state: {
                      ...builderState,
                      battery: batteryToSave
                    }
                  });
                }
              }}
              style={{
                width: '100%',
                padding: '6px 8px',
                fontSize: '0.7em',
                background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
                border: 'none',
                color: 'white',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #0077dd 0%, #0055aa 100%)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #0066cc 0%, #004499 100%)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ lineHeight: '1.0' }}>🔧 Select & Build Setup</div>
              <div style={{
                fontSize: '0.85em',
                fontWeight: '400',
                opacity: 0.9,
                lineHeight: '1.0'
              }}>
                Add screen, transducer, etc.
              </div>
            </button>
          </div>
        </div>
                    </div>
                  </div>
                );
              })}
            </GridContainer>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {modalImage.isOpen && (
      <ImageModal 
        isOpen={modalImage.isOpen}
        imageUrl={modalImage.url}
        altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
      />
      )}

      {/* Onboarding Tooltip Overlay */}
      {showOnboardingTooltip && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={() => setShowOnboardingTooltip(false)}
        >
          <div 
            style={{
              position: 'relative',
              marginTop: '120px',
              marginLeft: window.innerWidth <= 768 ? '20px' : '280px',
              maxWidth: '350px',
              backgroundColor: '#2c3e50',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              animation: 'slideInFromLeft 0.3s ease-out',
              zIndex: 1001
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Arrow pointing to filters (desktop only) */}
            {window.innerWidth > 768 && (
              <div 
                style={{
                  position: 'absolute',
                  left: '-15px',
                  top: '30px',
                  width: 0,
                  height: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderRight: '15px solid #2c3e50'
                }}
              />
            )}
            
            <h4 style={{ 
              margin: '0 0 12px 0', 
              fontSize: '16px', 
              fontWeight: 'bold',
              color: '#3498db'
            }}>
              🔍 Start Here!
            </h4>
            
            <p style={{ 
              margin: '0 0 15px 0', 
              fontSize: '14px', 
              lineHeight: '1.4',
              color: '#ecf0f1'
            }}>
              Use these <strong>Filter Options</strong> to narrow down your battery choices based on what you need. Watch as the battery options update in real-time!
            </p>
            
            <p style={{ 
              margin: '0 0 15px 0', 
              fontSize: '13px', 
              color: '#bdc3c7',
              fontStyle: 'italic'
            }}>
              Try selecting a battery type or setting a minimum runtime to get started!
            </p>
            
            <button
              onClick={() => setShowOnboardingTooltip(false)}
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
            >
              Got it! 👍
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInFromLeft {
          from { 
            opacity: 0;
            transform: translateX(-30px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% { 
            box-shadow: 0 0 20px 4px rgba(52, 152, 219, 0.6), 2px 0 8px rgba(0, 0, 0, 0.2);
          }
          50% { 
            box-shadow: 0 0 30px 6px rgba(52, 152, 219, 0.8), 2px 0 8px rgba(0, 0, 0, 0.2);
          }
        }
        
        @keyframes showMoreSpecsGlow {
          0%, 100% { 
            box-shadow: 0 0 10px 2px rgba(52, 152, 219, 0.6);
          }
          50% { 
            box-shadow: 0 0 15px 3px rgba(52, 152, 219, 0.8);
          }
        }
      `}</style>
    </div>
  );
};

export default BatterySelection;
